/*
 * @providesModule FriendsTournamentApiController
 *
 */

import _ from 'lodash';
import CurrentUserStore from 'CurrentUserStore';
import NetClient, {ErrorResponse} from 'NetClient';
import RESTStore from 'RESTStore';
import Match from 'SKZMatch';
import SKZTournament from 'SKZTournament';

import {NativeModules} from 'react-native';

const OnError = NativeModules.SKZAPIManager.OnError;

class FriendsTournamentApiController {
  // The recently fetched tournaments for a given opponent user id.
  // Used for the display of UI that may need this quickly, such as the PlayerDetailModal
  recentFetchCache = new Map();

  fetchIncentiveData(useCache?: boolean): Promise {
    console.debug('FriendsTournamentApiController: fetch number of available challenges');
    return new Promise(
      (
        resolve: (response: {
          max_friend_challenges: number;
          remaining_friend_challenges: number;
          expiration_date: string;
        }) => void,
        reject: (error: unknown) => void
      ) => {
        const getResponse = useCache
          ? RESTStore.getAnyResponse.bind(RESTStore)
          : RESTStore.getServerResponse.bind(RESTStore);

        CurrentUserStore.getCurrentUserId().then((userId: string) => {
          getResponse(
            `users/${userId}/friends/challenges`,
            null,
            OnError.failSilently,
            (response: unknown, error: unknown) => {
              if (response && !error) {
                resolve(response);
              } else {
                reject(error);
              }
            }
          );
        });
      }
    );
  }

  joinTournament(
    userId: String,
    joinParams: Object,
    retry: unknown,
    callback: (joinErr?: ErrorResponse, payload?: Match) => void
  ) {
    console.debug(
      'FriendsTournamentApiController: join tournament: ' +
        userId +
        ' ' +
        JSON.stringify(joinParams)
    );
    const path = 'users/' + userId + '/matches/friends';
    NetClient.postURL(path, joinParams, retry, callback);
  }

  createAndJoinTournament(
    path: string,
    joinParams: Object,
    retry: unknown,
    callback: (joinErr?: Object, payload?: Object) => void
  ) {
    console.debug('FriendsTournamentApiController: create and join tournament');
    NetClient.postURL(path, joinParams, retry, callback);
  }

  nudgeOffSystemUser(
    currentUserId: number,
    phone: string,
    contactName: string,
    matchId: number
  ): Promise {
    console.debug(
      'FriendsTournamentApiController: Nudge User: ' + currentUserId + ' nudged phone ' + phone
    );
    return new Promise((resolve: () => void, reject: (error: unknown) => void) => {
      const body = {
        phone: phone,
        contact_name: contactName,
      };

      NetClient.patchURL(
        `users/${currentUserId}/matches/${matchId}/friend`,
        body,
        OnError.promptRetry,
        (error: unknown, payload: unknown) => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        }
      );
    });
  }

  nudgeUser(currentUserId: number, invitedUserId: number, matchId: number): Promise {
    console.debug(
      'FriendsTournamentApiController: Nudge User: ' + currentUserId + ' nudged ' + invitedUserId
    );
    return new Promise((resolve: () => void, reject: (error: unknown) => void) => {
      const body = {user_id: invitedUserId};

      NetClient.patchURL(
        `users/${currentUserId}/matches/${matchId}/friend`,
        body,
        OnError.promptRetry,
        (error: unknown, payload: unknown) => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        }
      );
    });
  }

  addFriendsToExistingTournament(matchID: string, friendIDsToAdd: Array): Promise {
    console.debug('FriendsTournamentApiController: add friends to existing tournament');
    return new Promise((resolve: () => void, reject: (error: unknown) => void) => {
      const body = {user_id_list: friendIDsToAdd};

      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        NetClient.patchURL(
          `users/${userId}/matches/${matchID}/friends`,
          body,
          OnError.failSilently,
          (error: unknown, payload: unknown) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          }
        );
      });
    });
  }

  fetchUser(): Promise {
    return CurrentUserStore.fetchUser(true);
  }

  fetchTournamentData(opponentUserId: string): Promise {
    return new Promise((resolve: (response: Object) => void, reject: (error: unknown) => void) => {
      let params = null;
      if (opponentUserId && typeof opponentUserId == 'string' && opponentUserId.length > 0) {
        params = {opponent_user_id: opponentUserId};
      }
      RESTStore.getServerResponse(
        'tournaments/friends',
        params,
        OnError.failSilently,
        (response: Object, err: unknown) => {
          console.debug(
            'FriendsTournamentApiController: fetchTournamentData: err: ' +
              JSON.stringify(err) +
              ' | response: ' +
              JSON.stringify(response)
          );
          if (err) {
            reject(err);
            return;
          }
          response.tournaments = SKZTournament.make(_.get(response, 'tournaments'));
          if (response.highlight_text) {
            for (const tournament of response.tournaments) {
              if (tournament.shouldShowFreeEntryBanner()) {
                tournament.highlight_text = response.highlight_text;
              }
            }
          }
          this.recentFetchCache.set(opponentUserId, response.tournaments);
          resolve(response);
        }
      );
    });
  }

  getFetchedTournamentsForOpponent(opponentUserId: string): Array<SKZTournament> {
    return this.recentFetchCache.get(opponentUserId) ?? [];
  }

  //return an array of invited friends.
  fetchInvitedFriends(userId: number, matchId: number): Promise<Object> {
    return new Promise(
      (resolve: (response: Array<string>) => void, reject: (error: unknown) => void) => {
        RESTStore.getServerResponse(
          `users/${userId}/matches/${matchId}/friends`,
          null,
          OnError.failSilently,
          (response: Object, err: unknown) => {
            if (err) {
              reject(err);
            } else {
              resolve(response);
            }
          }
        );
      }
    );
  }

  // returns an array of recommended friends for a user
  fetchRecommendedFriends(params: Object): Promise {
    return new Promise(
      (resolve: (response: Array<Object>) => void, reject: (error: unknown) => void) => {
        RESTStore.getServerResponse(
          `users/${params.user_id}/${params?.tournament_id}/recommended_friends`,
          null,
          OnError.failSilently,
          (response: Object, err: unknown) => {
            if (err) {
              reject(err);
            } else {
              resolve(response);
            }
          }
        );
      }
    );
  }

  fetchTournamentForCode(matchCode: String): Promise {
    return new Promise(
      (resolve: (response: Array<SKZTournament>) => void, reject: (error: unknown) => void) => {
        const params = {
          match_code: matchCode,
        };

        RESTStore.getServerResponse(
          'tournaments/',
          params,
          OnError.failSilently,
          (response: Object, err: unknown) => {
            if (err) {
              reject(err);
            } else {
              resolve(response);
            }
          }
        );
      }
    );
  }

  addIncentiveChallengeToFriend(targetUserId: string): Promise {
    return new Promise((resolve: () => void, reject: (error: unknown) => void) => {
      const body = {friend_user_id: targetUserId};
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        NetClient.postURL(
          `users/${userId}/friends`,
          body,
          OnError.failSilently,
          (error: unknown, payload: unknown) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          }
        );
      });
    });
  }
}

export default new FriendsTournamentApiController();
